import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ICON_LIBRARY } from '../iconLibrary';
import type { IconName } from '../types';
import { CanvasIcon } from '../render/CanvasIcon';

export function IconPicker({
  value = 'none',
  onChange,
  label = 'Icoon',
}: {
  value?: IconName;
  onChange: (value: IconName) => void;
  label?: string;
}) {
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();
  const results = useMemo(
    () => ICON_LIBRARY.filter((item) => !normalized || `${item.label} ${item.value} ${item.category} ${item.keywords}`.toLowerCase().includes(normalized)),
    [normalized],
  );
  const selected = ICON_LIBRARY.find((item) => item.value === value) ?? ICON_LIBRARY[0]!;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <span className="text-[11px] font-medium text-slate-400">Lucide · {ICON_LIBRARY.length - 1} iconen</span>
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Zoek een icoon…"
          aria-label="Zoek in iconenbibliotheek"
          className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-9 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
        />
        {query ? <button type="button" onClick={() => setQuery('')} aria-label="Zoekopdracht wissen" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button> : null}
      </div>
      <div className="grid max-h-64 grid-cols-5 gap-1.5 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2" role="listbox" aria-label="Iconenbibliotheek">
        {results.map((item) => (
          <button
            key={item.value}
            type="button"
            role="option"
            aria-selected={item.value === value}
            title={item.label}
            onClick={() => onChange(item.value)}
            className={cn(
              'flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg border bg-white px-1 py-1.5 text-[10px] font-medium leading-tight transition',
              item.value === value ? 'border-teal-400 bg-teal-50 text-teal-800 ring-1 ring-teal-300' : 'border-slate-200 text-slate-600 hover:border-teal-300 hover:bg-teal-50/60',
            )}
          >
            {item.value === 'none' ? <span className="text-lg leading-none text-slate-300">∅</span> : <CanvasIcon name={item.value} className="h-5 w-5" />}
            <span className="max-w-full truncate">{item.label}</span>
          </button>
        ))}
        {!results.length ? <p className="col-span-5 p-3 text-center text-xs text-slate-500">Geen passend icoon gevonden.</p> : null}
      </div>
      <p className="text-[11px] leading-relaxed text-slate-500">Gekozen: <span className="font-semibold text-slate-700">{selected.label}</span>. De iconen zijn schaalbaar en volgen automatisch de kleur van het element.</p>
    </div>
  );
}
